import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge, Card, CopyButton, EmptyState, Input, Spinner } from '../../components/ui'
import { api } from '../../lib/api'
import { datetime, prettyIban } from '../../lib/format'

interface HistoryItem {
  id: string
  shortUrl: string | null
  version: string
  label: string | null
  beneficiaryName: string | null
  iban: string | null
  amount: string | null
  payload: string
  scanCount: number
  createdAt: string
}

interface HistoryResponse {
  items: HistoryItem[]
  total: number
  limit: number
  offset: number
}

const PAGE_SIZE = 25

export function History() {
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)

  const { data, isLoading } = useQuery({
    queryKey: ['qr-history', search, offset],
    queryFn: () =>
      api.get<HistoryResponse>(
        `/qr/documents?limit=${PAGE_SIZE}&offset=${offset}` +
          (search ? `&search=${encodeURIComponent(search)}` : ''),
      ),
  })

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Історія QR</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Тут зберігаються QR, створені з <code className="code">save: true</code>.
          </p>
        </div>
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOffset(0)
          }}
          placeholder="Пошук за отримувачем, IBAN або міткою"
          className="max-w-xs"
        />
      </header>

      {isLoading ? (
        <div className="grid place-items-center py-24">
          <Spinner className="h-8 w-8 text-brand-600" />
        </div>
      ) : !data?.items.length ? (
        <Card>
          <EmptyState
            title={search ? 'Нічого не знайдено' : 'Історія порожня'}
            description={
              search
                ? 'Спробуйте інший запит.'
                : 'Передайте save: true у запиті на генерацію — і QR потрапить сюди разом із коротким посиланням на сторінку оплати.'
            }
            action={
              !search ? (
                <Link to="/docs#generate" className="btn-secondary mt-2">
                  Як це працює
                </Link>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {data.items.map((item) => (
              <Card key={item.id} className="flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold">
                      {item.beneficiaryName ?? 'Без назви'}
                    </h3>
                    {item.label && (
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                        {item.label}
                      </p>
                    )}
                  </div>
                  <Badge tone="brand">{item.version}</Badge>
                </div>

                {item.iban && (
                  <p className="mt-3 break-all font-mono text-xs text-slate-500 dark:text-slate-400">
                    {prettyIban(item.iban)}
                  </p>
                )}

                <div className="mt-3 flex items-baseline justify-between">
                  <span className="text-lg font-bold">
                    {item.amount ? `${item.amount} ₴` : 'Сума не вказана'}
                  </span>
                  <span className="text-xs text-slate-400">
                    {item.scanCount > 0 ? `${item.scanCount} переглядів` : 'без переглядів'}
                  </span>
                </div>

                <div className="flex-1" />

                <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <span>{datetime(item.createdAt)}</span>
                  <div className="flex gap-1">
                    <CopyButton value={item.payload} label="payload" />
                    {item.shortUrl && (
                      <a
                        href={item.shortUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg px-2 py-1 font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-950"
                      >
                        Відкрити ↗
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {data.total > PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} із {data.total}
              </p>
              <div className="flex gap-2">
                <button
                  className="btn-secondary text-sm"
                  disabled={offset === 0}
                  onClick={() => setOffset((v) => Math.max(0, v - PAGE_SIZE))}
                >
                  Назад
                </button>
                <button
                  className="btn-secondary text-sm"
                  disabled={offset + PAGE_SIZE >= data.total}
                  onClick={() => setOffset((v) => v + PAGE_SIZE)}
                >
                  Далі
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
